import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, NgZone, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MainStore } from 'src/app/common/main.store';
import { CfgCellDirective } from 'src/app/common/table/table-cell.directive';
import { TableColumn } from 'src/app/common/table/table-column.model';
import { tableResource } from 'src/app/common/table/table-resource';
import { CfgTableComponent } from 'src/app/common/table/table.component';
import { CommonService } from 'src/app/services/common.service';
import { DepartmentService } from 'src/app/services/department.service';
import { ModalService } from 'src/app/services/modal.service';
import { environment } from 'src/environments/environment';
import { Customer, ProvisioningStatus, TenantProvisioningStatus } from '../customer.model';
import { CustomerService } from '../customer.service';

// Per-row provisioning view model — derived once from signals via a computed so the template only reads
// data (no method calls, which would re-run on every change-detection pass in this zone-based app).
interface RowProvisioning {
  state: 'provisioning' | 'ready' | 'failed';
  completing: boolean;
  errorMessage: string | null;
}

// Rækkerne tabellen viser: kunden med provisioning-overlayet joinet på.
interface CustomerRow extends Customer {
  provisioning?: RowProvisioning;
}

@Component({
  selector: 'app-customer-list',
  templateUrl: './customer-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CfgTableComponent, CfgCellDirective]
})
export class CustomerListComponent {

  readonly userIsAdmin = signal(false);

  // Tenant-provisioning overlay: tenantId -> current (non-completed) provisioning state. Populated from
  // GET /customer/provisionings and polled while any are in flight.
  readonly provisioning = signal<Record<number, ProvisioningStatus>>({});
  private readonly completingIds = signal<Set<number>>(new Set());
  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private lastSignature = '';

  readonly provisioningView = computed<Record<number, RowProvisioning>>(() => {
    const map = this.provisioning();
    const completing = this.completingIds();
    const view: Record<number, RowProvisioning> = {};
    for (const key of Object.keys(map)) {
      const id = Number(key);
      const p = map[id];
      const state: RowProvisioning['state'] =
        p.status === TenantProvisioningStatus.Failed ? 'failed'
          : p.isTenantDbReady ? 'ready'
            : 'provisioning';
      view[id] = { state, completing: completing.has(id), errorMessage: p.errorMessage };
    }
    return view;
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  private readonly customerService = inject(CustomerService);
  private readonly store = inject(MainStore);
  public readonly modalService = inject(ModalService);
  private readonly commonService = inject(CommonService);
  private readonly departmentService = inject(DepartmentService);
  private readonly router = inject(Router);

  private readonly departmentGuid = computed(() => {
    const selected = this.departmentService.selectedDepartment();
    if (selected?.id && selected.id !== this.commonService.guidEmpty()) {
      return selected.id;
    }
    return this.store.currentUser.get().departmentId;
  });

  protected readonly customersResource = tableResource<Customer>(() => {
    const departmentGuid = this.departmentGuid();
    const params: Record<string, string> = {};
    if (departmentGuid) {
      params['departmentGuid'] = departmentGuid;
    }
    return { url: `${environment.adminConfig.apiUri}/customer/customers`, params };
  });

  // Provisioning-overlayet joines på kunderne — [rows] overstyrer kun visningen, aldrig tilstanden.
  protected readonly rows = computed<CustomerRow[]>(() => {
    const view = this.provisioningView();
    return (this.customersResource.value() ?? []).map(customer => ({
      ...customer,
      provisioning: view[customer.customerId],
    }));
  });

  protected readonly columns = computed<TableColumn<CustomerRow>[]>(() => [
    { key: 'customerId', header: 'Id', width: '80px', type: 'number' },
    { key: 'name', header: 'Kundenavn' },
    ...(this.userIsAdmin()
      ? ([
          { key: 'departmentName', header: 'Afdelingsnavn', width: '200px' },
          { key: 'departmentCode', header: 'Afdelingskode', width: '100px' },
        ] satisfies TableColumn<CustomerRow>[])
      : []),
    { key: 'status', header: '', sortable: false, cellClass: 'text-end text-nowrap' },
  ]);

  protected readonly trackById = (row: CustomerRow) => row.customerId;
  // Rows with provisioning in flight have no dashboard link — the status button is the only action.
  // Rows without a database (no Kunde row / no tenant DB in this environment) have no link either:
  // opening them would just fail.
  protected readonly rowLink = (row: CustomerRow) =>
    (row.provisioning || !row.hasDatabase ? null : ['/customer-dashboard', row.customerId]);
  protected readonly rowLabel = (row: CustomerRow) => `Åbn ${row.name}`;

  constructor() {
    this.destroyRef.onDestroy(() => this.stopPolling());

    // Overlay provisioning state whenever a (re)loaded customer list arrives.
    effect(() => {
      if (this.customersResource.value() !== undefined) {
        untracked(() => void this.refreshProvisionings());
      }
    });

    this.commonService.CustomerListChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (changed: string) => {
        // 'nykunde' = new customer; a number = a company was added to that customer.
        if (changed == 'nykunde' || (!Number.isNaN(changed) && Number(changed) > 0)) {
          this.customersResource.reload();
        }
      }
    });
  }

  fastAction(filtered: readonly CustomerRow[]): void {
    // Only one result? Navigate to it — unless the row has no navigation (provisioning / no database).
    if (filtered.length === 1 && !filtered[0].provisioning && filtered[0].hasDatabase) {
      this.router.navigate(['/customer-dashboard', filtered[0].customerId]);
    }
  }

  //#region Tenant provisioning overlay

  async finishSetup(customerId: number): Promise<void> {
    this.completingIds.update(s => new Set(s).add(customerId));
    try {
      await this.customerService.completeProvisioning(customerId);
      // The row is already in the list; dropping it from the provisioning overlay relinks it to the
      // dashboard — no need to reload the whole customer list (which would flash the loader).
      await this.refreshProvisionings();
    } finally {
      this.completingIds.update(s => { const next = new Set(s); next.delete(customerId); return next; });
    }
  }

  private async refreshProvisionings(): Promise<void> {
    try {
      const list = await this.customerService.getPendingProvisionings(this.departmentGuid());

      // Only touch the signal when the data actually changed, so steady-state polling doesn't schedule
      // change detection. Signature covers the fields the view depends on.
      const signature = list
        .map(p => `${p.tenantId}:${p.status}:${p.isTenantDbReady ? 1 : 0}:${p.errorMessage ?? ''}`)
        .sort()
        .join('|');

      if (signature !== this.lastSignature) {
        this.lastSignature = signature;
        const map: Record<number, ProvisioningStatus> = {};
        for (const p of list) {
          map[p.tenantId] = p;
        }
        // Re-enter the zone to apply the update (the poll timer runs outside it).
        this.zone.run(() => this.provisioning.set(map));
      }

      // Only a background workflow advances an in-flight provisioning; failed/ready rows
      // change solely on a user action (which re-runs this method), so polling them is
      // pointless. Keep the timer alive only while something is still in flight.
      const anyInFlight = list.some(
        p => p.status !== TenantProvisioningStatus.Failed && !p.isTenantDbReady);
      if (anyInFlight) {
        this.ensurePolling();
      } else {
        this.stopPolling();
      }
    } catch {
      // Transient errors are surfaced by the global interceptor; keep any running interval.
    }
  }

  // The timer runs OUTSIDE Angular's zone so its ticks don't trigger change detection; a tick only
  // schedules CD when refreshProvisionings finds a change and re-enters the zone to set the signal.
  private ensurePolling(): void {
    if (this.pollHandle != null) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.pollHandle = setInterval(() => void this.refreshProvisionings(), 6000);
    });
  }

  private stopPolling(): void {
    if (this.pollHandle != null) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  //#endregion
}
